import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { gsap } from 'gsap';
import ProductCard from '../components/ProductCard';
import { products } from '../data/products';

// Import SVG icons
import houseplantIcon from '../assets/images/icons/houseplant.svg';
import fishingPoleIcon from '../assets/images/icons/fishing-pole.svg';
import boneIcon from '../assets/images/icons/bone.svg';
import healthIcon from '../assets/images/icons/health.svg';

const Products: React.FC = () => {
  const mobileContainerRef = useRef<HTMLDivElement>(null);
  const desktopContainerRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState(0);

  const formatPrice = useCallback((price: number) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  }, []);

  const nextCategory = useCallback(() => {
    setActiveCategory((prev) => (prev + 1) % 5);
  }, []);

  const prevCategory = useCallback(() => {
    setActiveCategory((prev) => (prev - 1 + 5) % 5);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Анимация для мобильного контейнера
      if (mobileContainerRef.current) {
        const mobileElements = mobileContainerRef.current.children;
        gsap.set(mobileElements, { willChange: 'transform, opacity' });
        gsap.fromTo(mobileElements,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out',
            onComplete: () => { 
              gsap.set(mobileElements, { willChange: 'auto' }); 
            }
          }
        );
      }

      // Анимация для десктопного контейнера
      if (desktopContainerRef.current) {
        const desktopElements = desktopContainerRef.current.children;
        gsap.set(desktopElements, { willChange: 'transform, opacity' });
        gsap.fromTo(desktopElements,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out',
            onComplete: () => { 
              gsap.set(desktopElements, { willChange: 'auto' }); 
            }
          }
        );
      }
    });

    return () => {
      ctx.revert();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Заголовок */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-4 transition-all duration-300 hover:scale-105 group"
          >
            <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
            <span>Вернуться на главную</span>
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            Наша продукция
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mt-4 max-w-3xl">
            Широкий ассортимент экологически чистых продуктов для различных нужд. 
            От натуральных удобрений до биологически активных добавок для здоровья.
          </p>
        </div>

        {/* Продукты для мобильных - 2 колонки */}
        <div ref={mobileContainerRef} className="md:hidden grid grid-cols-2 gap-4 mb-12">
          {products.map((product, index) => (
            <div key={product.id} className="card group overflow-hidden">
              {/* Изображение */}
              <div className="relative overflow-hidden">
                <Link to={`/product/${product.id}`} state={{ from: 'products' }}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                  />
                </Link>
                {!product.inStock && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    Под заказ
                  </div>
                )}
              </div>

              {/* Контент */}
              <div className="p-4">
                <Link to={`/product/${product.id}`} state={{ from: 'products' }}>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 h-10 flex items-center group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200 cursor-pointer">
                    {product.name}
                  </h3>
                </Link>
                
                {/* Цена с боксом глаза справа */}
                {product.price && (
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-base font-bold text-primary-600 dark:text-primary-400">
                      {formatPrice(product.price)} ₽
                    </span>
                    
                    {/* Бокс с иконкой глаза для мобильных */}
                    <Link
                      to={`/product/${product.id}`}
                      state={{ from: 'products' }}
                      className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors duration-200"
                    >
                      <Eye className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </Link>
                  </div>
                )}


              </div>
            </div>
          ))}
        </div>

        {/* Продукты для десктопа - оригинальный дизайн */}
        <div ref={desktopContainerRef} className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {products.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} from="products" />
          ))}
        </div>

        {/* Информация о категориях */}
        <div className="mt-16 bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Категории продукции
          </h2>
          
          {/* Мобильная версия - слайдер */}
          <div className="md:hidden">
            <div className="relative">
              {/* Карточка категории */}
              <div className="text-center px-4">
                {activeCategory === 0 && (
                  <>
                    <div className="flex justify-center mb-6">
                      <img src={houseplantIcon} alt="Удобрения" className="w-16 h-16" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      Удобрения
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      Натуральные органические удобрения для растений, способствующие здоровому росту и развитию
                    </p>
                  </>
                )}

                {activeCategory === 1 && (
                  <>
                    <div className="flex justify-center mb-6">
                      <img src={fishingPoleIcon} alt="Рыболовные товары" className="w-16 h-16" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      Рыболовные товары
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      Эффективные прикормки с натуральными компонентами для успешной рыбалки
                    </p>
                  </>
                )}

                {activeCategory === 2 && (
                  <>
                    <div className="flex justify-center mb-6">
                      <img src={boneIcon} alt="Корма для животных" className="w-16 h-16" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      Корма для животных
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      Витаминно-минеральные добавки для домашних и сельскохозяйственных животных
                    </p>
                  </>
                )}

                {activeCategory === 3 && (
                  <>
                    <div className="flex justify-center mb-6">
                      <img src={healthIcon} alt="БАДы" className="w-16 h-16" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      БАДы
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      Биологически активные добавки для здоровья и поддержания жизненного тонуса
                    </p>
                  </>
                )}

                {activeCategory === 4 && (
                  <>
                    <div className="flex justify-center mb-6">
                      <img src={boneIcon} alt="Сельскохозяйственные животные" className="w-16 h-16" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      Сельскохозяйственные животные
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      Специализированные добавки для повышения продуктивности КРС, свиней и птицы
                    </p>
                  </>
                )}
              </div>

              {/* Стрелки навигации */}
              <button
                onClick={prevCategory}
                className="absolute left-0 top-1/2 -translate-y-1/2 p-3 bg-white dark:bg-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                aria-label="Предыдущая категория"
              >
                <ChevronLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </button>

              <button
                onClick={nextCategory}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-3 bg-white dark:bg-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                aria-label="Следующая категория"
              >
                <ChevronRight className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            {/* Индикаторы слайдов */}
            <div className="flex justify-center space-x-2 mt-8">
              {[0, 1, 2, 3, 4].map((index) => (
                <button
                  key={index}
                  onClick={() => setActiveCategory(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === activeCategory
                      ? 'bg-primary-600 dark:bg-primary-400'
                      : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }`}
                  aria-label={`Перейти к категории ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Десктопная версия - оригинальный grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="text-center p-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <img src={houseplantIcon} alt="Удобрения" className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Удобрения</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Натуральные органические удобрения для растений
              </p>
            </div>

            <div className="text-center p-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <img src={fishingPoleIcon} alt="Рыболовные товары" className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Рыболовные товары</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Эффективные прикормки с натуральными компонентами
              </p>
            </div>

            <div className="text-center p-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <img src={boneIcon} alt="Корма для животных" className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Корма для животных</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Витаминно-минеральные добавки для домашних животных
              </p>
            </div>

            <div className="text-center p-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <img src={healthIcon} alt="БАДы" className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">БАДы</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Биологически активные добавки для здоровья
              </p>
            </div>

            <div className="text-center p-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <img src={boneIcon} alt="Сельскохозяйственные животные" className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">С/х животные</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Специализированные добавки для КРС, свиней и птицы
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
